/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { Avatar } from "@base-ui/react/avatar"
import clsx from "clsx"
import { AppWindowIcon } from "lucide-react"
import { observer } from "mobx-react-lite"

import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { useViewModel } from "../app/app-hooks"

import "./app-icon.css"

const ICON_URL_PREFIX = `${process.env.API_URL}/api/private/application-type/`

/**
 * Builds the URL for an application type's icon.
 *
 * The API allows browsers to cache icons for one day. The `v` query parameter
 * increments with each upload during the page session, giving each updated icon
 * a new URL so the browser fetches it immediately. The parameter is omitted
 * until the first upload to keep the URL stable for unchanged icons.
 *
 * The version resets on page reload, so the browser may reuse a previously
 * cached icon until its cache expires.
 */
const iconUrl = (appTypeId: number, version: number) =>
    `${ICON_URL_PREFIX}${appTypeId}/icon${version === 0 ? "" : `?v=${version}`}`

interface Props {
    /**
     * Application type ID, or `undefined` if the API response omits it.
     * When undefined, AppIcon displays the placeholder without making a request.
     */
    appTypeId: number | undefined
    /**
     * Additional classes.
     */
    className?: string
    /**
     * Accessible name for the icon, and for the placeholder that stands in for it.
     * Leave it empty when text next to the icon already names the application.
     *
     * @default ""
     */
    alt?: string
}

/**
 * Renders an application type's icon using Base UI's Avatar.
 *
 * A muted tile with Lucide's `AppWindowIcon` serves as the placeholder while the
 * image loads and when no icon is available. The image fades in when loaded,
 * and an upload changes its URL so the updated icon is fetched.
 */
export const AppIcon = observer(({ appTypeId, alt = "", className }: Props) => {
    const appTypeVM = useViewModel(ApplicationTypeViewModel)

    const src = appTypeId === undefined ? undefined : iconUrl(appTypeId, appTypeVM.iconVersionFor(appTypeId))

    return (
        <Avatar.Root
            data-slot="app-icon"
            className={clsx("app-icon has-[svg]:bg-muted has-[svg]:text-muted-foreground", className)}
        >
            <Avatar.Image
                src={src}
                alt={alt}
                className="size-full object-cover transition-opacity duration-150 ease-out data-starting-style:opacity-0 motion-reduce:transition-none"
            />
            {/*
             * Render the placeholder SVG directly so the root can centre it and
             * style the tile with `has-[svg]:`. When `alt` is provided, expose
             * the placeholder as an image with that accessible name; otherwise,
             * hide it from assistive technology like the decorative icon.
             */}
            <Avatar.Fallback
                render={<AppWindowIcon />}
                role={alt ? "img" : undefined}
                aria-label={alt || undefined}
                aria-hidden={alt ? undefined : true}
                className="size-3/5"
            />
        </Avatar.Root>
    )
})
